import { getSupabaseClient } from "./index";
import {
  MENU_IMAGE_MAX_BYTES,
  normalizeMenuTags,
  type MenuCategoryRecord,
  type MenuItemRecord,
  type MenuItemWithModifiers,
  type MenuModifierGroupRecord,
  type MenuModifierOptionRecord,
  type MenuPromotionRecord,
  type MenuTag,
  type ModifierGroupDraft,
  type ModifierSelectionType,
  type PublicMenuPayload,
} from "@veorest/utils";

export type {
  MenuCategoryRecord,
  MenuItemRecord,
  MenuItemWithModifiers,
  MenuModifierGroupRecord,
  MenuModifierOptionRecord,
  MenuPromotionRecord,
  MenuTag,
  ModifierGroupDraft,
  PublicMenuPayload,
};

function mapCategory(row: Record<string, unknown>): MenuCategoryRecord {
  return row as unknown as MenuCategoryRecord;
}

function mapItem(row: Record<string, unknown>): MenuItemRecord {
  return {
    ...(row as unknown as MenuItemRecord),
    tags: normalizeMenuTags(row.tags as string[]),
    base_price: Number(row.base_price),
  };
}

function mapPromotion(row: Record<string, unknown>): MenuPromotionRecord {
  return {
    ...(row as unknown as MenuPromotionRecord),
    discount_value: row.discount_value != null ? Number(row.discount_value) : null,
  };
}

export async function listMenuCategories(tenantId: string): Promise<MenuCategoryRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_categories")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("sort_order")
    .order("name");
  if (error) throw error;
  return (data ?? []).map(mapCategory);
}

export async function createMenuCategory(
  tenantId: string,
  input: { name: string; description?: string | null; is_available?: boolean; sort_order?: number }
): Promise<MenuCategoryRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_categories")
    .insert({
      tenant_id: tenantId,
      name: input.name.trim(),
      description: input.description?.trim() || null,
      sort_order: input.sort_order ?? 0,
      is_available: input.is_available ?? true,
    })
    .select("*")
    .single();
  if (error) throw error;
  return mapCategory(data);
}

export async function updateMenuCategory(
  id: string,
  patch: Partial<Pick<MenuCategoryRecord, "name" | "sort_order" | "is_available" | "description" | "image_url">>
): Promise<MenuCategoryRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("menu_categories").update(patch).eq("id", id).select("*").single();
  if (error) throw error;
  return mapCategory(data);
}

export async function deleteMenuCategory(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("menu_categories").delete().eq("id", id);
  if (error) throw error;
}

export async function reorderMenuCategories(
  tenantId: string,
  orderedIds: string[]
): Promise<void> {
  const supabase = getSupabaseClient();
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase.from("menu_categories").update({ sort_order: index }).eq("id", id).eq("tenant_id", tenantId)
    )
  );
}

export async function listMenuItems(tenantId: string): Promise<MenuItemRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_items")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("sort_order")
    .order("name");
  if (error) throw error;
  return (data ?? []).map(mapItem);
}

export async function listMenuModifierGroups(itemId: string): Promise<MenuModifierGroupRecord[]> {
  const supabase = getSupabaseClient();
  const { data: groups, error } = await supabase
    .from("menu_modifier_groups")
    .select("*")
    .eq("item_id", itemId)
    .order("sort_order");
  if (error) throw error;
  if (!groups?.length) return [];

  const groupIds = groups.map((g) => g.id);
  const { data: options, error: optErr } = await supabase
    .from("menu_modifier_options")
    .select("*")
    .in("group_id", groupIds)
    .order("sort_order");
  if (optErr) throw optErr;

  return groups.map((g) => ({
    ...(g as MenuModifierGroupRecord),
    options: (options ?? [])
      .filter((o) => o.group_id === g.id)
      .map((o) => ({
        ...(o as MenuModifierOptionRecord),
        price_delta: Number(o.price_delta),
      })),
  }));
}

export async function getMenuItemWithModifiers(itemId: string): Promise<MenuItemWithModifiers | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("menu_items").select("*").eq("id", itemId).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const modifier_groups = await listMenuModifierGroups(itemId);
  return { ...mapItem(data), modifier_groups };
}

export async function createMenuItem(
  tenantId: string,
  input: {
    category_id: string;
    name: string;
    description?: string | null;
    base_price: number;
    currency: string;
    image_url?: string | null;
    tags?: MenuTag[];
    is_available?: boolean;
    sort_order?: number;
  }
): Promise<MenuItemRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_items")
    .insert({
      tenant_id: tenantId,
      category_id: input.category_id,
      name: input.name.trim(),
      description: input.description?.trim() || null,
      base_price: input.base_price,
      currency: input.currency,
      image_url: input.image_url ?? null,
      tags: input.tags ?? [],
      is_available: input.is_available ?? true,
      sort_order: input.sort_order ?? 0,
    })
    .select("*")
    .single();
  if (error) throw error;
  return mapItem(data);
}

export async function updateMenuItem(
  id: string,
  patch: Partial<
    Pick<
      MenuItemRecord,
      | "category_id"
      | "name"
      | "description"
      | "base_price"
      | "currency"
      | "image_url"
      | "tags"
      | "is_available"
      | "is_featured"
      | "sort_order"
      | "prep_time_minutes"
    >
  >
): Promise<MenuItemRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("menu_items").update(patch).eq("id", id).select("*").single();
  if (error) throw error;
  return mapItem(data);
}

export async function toggleMenuItemAvailability(id: string, isAvailable: boolean): Promise<MenuItemRecord> {
  return updateMenuItem(id, { is_available: isAvailable });
}

export async function deleteMenuItem(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("menu_items").delete().eq("id", id);
  if (error) throw error;
}

export async function reorderMenuItems(
  tenantId: string,
  categoryId: string,
  orderedIds: string[]
): Promise<void> {
  const supabase = getSupabaseClient();
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from("menu_items")
        .update({ sort_order: index })
        .eq("id", id)
        .eq("tenant_id", tenantId)
        .eq("category_id", categoryId)
    )
  );
}

export async function saveMenuModifierGroups(
  tenantId: string,
  itemId: string,
  groups: ModifierGroupDraft[]
): Promise<MenuModifierGroupRecord[]> {
  const supabase = getSupabaseClient();

  const { data: existingGroups } = await supabase
    .from("menu_modifier_groups")
    .select("id")
    .eq("item_id", itemId);

  const existingIds = new Set((existingGroups ?? []).map((g) => g.id));
  const incomingIds = new Set(groups.filter((g) => g.id).map((g) => g.id!));

  const toDelete = [...existingIds].filter((id) => !incomingIds.has(id));
  if (toDelete.length) {
    await supabase.from("menu_modifier_groups").delete().in("id", toDelete);
  }

  for (const [gIndex, group] of groups.entries()) {
    let groupId = group.id;
    const groupPayload = {
      tenant_id: tenantId,
      item_id: itemId,
      name: group.name.trim(),
      selection_type: group.selection_type as ModifierSelectionType,
      is_required: group.is_required,
      sort_order: gIndex,
    };

    if (groupId) {
      await supabase.from("menu_modifier_groups").update(groupPayload).eq("id", groupId);
    } else {
      const { data: created, error } = await supabase
        .from("menu_modifier_groups")
        .insert(groupPayload)
        .select("id")
        .single();
      if (error) throw error;
      groupId = created.id;
    }

    const { data: existingOptions } = await supabase
      .from("menu_modifier_options")
      .select("id")
      .eq("group_id", groupId);

    const existingOptIds = new Set((existingOptions ?? []).map((o) => o.id));
    const incomingOptIds = new Set(group.options.filter((o) => o.id).map((o) => o.id!));
    const optsToDelete = [...existingOptIds].filter((id) => !incomingOptIds.has(id));
    if (optsToDelete.length) {
      await supabase.from("menu_modifier_options").delete().in("id", optsToDelete);
    }

    for (const [oIndex, option] of group.options.entries()) {
      if (!option.name.trim()) continue;
      const optPayload = {
        group_id: groupId,
        name: option.name.trim(),
        price_delta: option.price_delta,
        is_available: option.is_available,
        sort_order: oIndex,
      };
      if (option.id) {
        await supabase.from("menu_modifier_options").update(optPayload).eq("id", option.id);
      } else {
        await supabase.from("menu_modifier_options").insert(optPayload);
      }
    }
  }

  return listMenuModifierGroups(itemId);
}

export async function listMenuPromotions(tenantId: string): Promise<MenuPromotionRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_promotions")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(mapPromotion);
}

export async function createMenuPromotion(
  tenantId: string,
  input: Omit<MenuPromotionRecord, "id" | "tenant_id" | "created_at">
): Promise<MenuPromotionRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("menu_promotions")
    .insert({ tenant_id: tenantId, ...input })
    .select("*")
    .single();
  if (error) throw error;
  return mapPromotion(data);
}

export async function updateMenuPromotion(
  id: string,
  patch: Partial<Omit<MenuPromotionRecord, "id" | "tenant_id" | "created_at">>
): Promise<MenuPromotionRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("menu_promotions").update(patch).eq("id", id).select("*").single();
  if (error) throw error;
  return mapPromotion(data);
}

export async function deleteMenuPromotion(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("menu_promotions").delete().eq("id", id);
  if (error) throw error;
}

export async function uploadMenuImage(tenantId: string, file: File): Promise<string> {
  if (file.size > MENU_IMAGE_MAX_BYTES) {
    throw new Error("image_too_large");
  }
  const supabase = getSupabaseClient();
  const ext = file.name.split(".").pop() || "jpg";
  const path = `${tenantId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("menu-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("menu-images").getPublicUrl(path);
  return data.publicUrl;
}

export async function getPublicMenu(slug: string): Promise<PublicMenuPayload> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_public_menu", { p_tenant_slug: slug });
  if (error) throw error;
  const payload = (data ?? {}) as PublicMenuPayload;
  return {
    tenant: payload.tenant ?? null,
    categories: payload.categories ?? [],
    items: (payload.items ?? []).map((item) => ({
      ...item,
      base_price: Number(item.base_price),
      tags: normalizeMenuTags(item.tags as string[]),
    })),
    promotions: payload.promotions ?? [],
  };
}
